import React, { useContext, useEffect, useState } from "react"
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import CreateFranchiseUserForm from "./CreateFranchiseUserForm"
import { Terminal } from "../../infrastructure/models/terminal"
import { SessionService } from "../../infrastructure/services/session"
import { container } from "../../infrastructure/container"
import { Logger } from "../../infrastructure/services/logger"
import { FranchiseUserService } from "../../infrastructure/services/api/franchiseUsers"
import { FranchiseUserBriefResponse } from "../../infrastructure/models/transport/response/FranchiseUserBriefResponse"
import { FranchiseRole } from "../../infrastructure/models/common/FranchiseRole"
import { COLORS } from "../../presentation/constants/colors"
import Searchbar from "./Searchbar"
import { SearchContext } from "../../presentation/context/SearchContext"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import { Pencil, Trash2 } from "lucide-react"
import UpdateFranchiseUserForm from "./UpdateFranchiseUserForm"
import { CreateFranchiseUsers } from "../../infrastructure/models/transport/request/CreateFranchiseUsers"

interface FranchiseUsersProps {
    franchiseId: string
    terminals: Terminal[]
}

const FranchiseUsers: React.FC<FranchiseUsersProps> = ({ franchiseId, terminals }) => {
    const sessionService = container.resolve<SessionService>("sessionService")
    const logger = container.resolve<Logger>("logger")
    const franchiseUserService = container.resolve<FranchiseUserService>("franchiseUserService")
    const navigate = useNavigate()

    const currentUser = sessionService.user
    const currentUserRole = currentUser?.role

    const [franchiseUsers, setFranchiseUsers] = useState<FranchiseUserBriefResponse[]>([])
    const [pageLoading, setPageLoading] = useState(false)
    const [apiLoading, setApiLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null)
    const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)
    const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false)
    const { searchQuery } = useContext(SearchContext)
    const isSuperAdmin = currentUser?.role === SupportRole.SUPERADMIN
    const [editMode, setEditMode] = useState(false)
    const [userToEdit, setUserToEdit] = useState<FranchiseUserBriefResponse | null>(null)

    const FranchiseRoleLabels: Record<FranchiseRole, string> = {
        [FranchiseRole.ADMIN]: "ADMIN",
        [FranchiseRole.MANAGER]: "MANAGER",
        [FranchiseRole.USER]: "USER",
        [FranchiseRole.DELIVERY]: "DELIVERY",
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setEditMode(false)
        setUserToEdit(null)
    }

    const [errorDialogOpen, setErrorDialogOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    // -------------------------------
    // Fetch franchise users automatically
    // -------------------------------
    useEffect(() => {
        if (!franchiseId) return
        (async () => {
            try {
                setPageLoading(true)
                const users = await franchiseUserService.getFranchiseUsers(franchiseId)
                setFranchiseUsers(users) // backend already filters by franchiseId
            } catch (err) {
                logger.error("Failed to load franchise users", err)
                setError("Failed to load franchise users")
            } finally {
                setPageLoading(false)
            }
        })()
    }, [franchiseId])

    const handleCreateFranchiseUser = async (data: any) => {
        setApiLoading(true)
        try {

            // Basic validation – check if required fields are empty
            if (
                !data.firstname?.trim() ||
                !data.lastname?.trim() ||
                !data.email?.trim() ||
                !data.phone?.primary?.trim() ||
                !data.role ||
                !data.address
            ) {
                setErrorMessage("We found some missing details. Complete the fields below and try again.")
                setErrorDialogOpen(true)
                setApiLoading(false)
                return
            }

            const created = await franchiseUserService.createFranchiseUsers(data)

            const brief: FranchiseUserBriefResponse = {
                id: created.email,
                franchiseId: created.franchiseId,
                firstname: created.firstname,
                lastname: created.lastname,
                email: created.email,
                phone: typeof created.phone === "string"
                    ? { primary: created.phone, secondary: "" }
                    : created.phone,
                role: created.role,
                terminals: Array.isArray(created.terminals)
                    ? created.terminals.map((t: any) =>
                        typeof t === "string" ? { terminalId: t, name: "Unknown" } : { terminalId: t.terminalId, name: t.name ?? "Unknown" }
                    )
                    : [],
                address: created.address,
                isActive: created.isActive ?? true,
            }

            setFranchiseUsers((prev) => [...prev, brief])
            setDialogOpen(false)
        } catch (err) {
            logger.error("Failed to create franchise user", err)
            setError("Failed to create franchise user")
        } finally {
            setApiLoading(false)
        }
    }

    const handleUpdateFranchiseUser = async (data: CreateFranchiseUsers) => {
        try {
            setApiLoading(true)

            const updated = await franchiseUserService.updateFranchiseUser(data)

            // Normalize phone so it’s always an object
            const normalizedUser: FranchiseUserBriefResponse = {
                ...updated,
                phone: typeof updated.phone === "string"
                    ? { primary: updated.phone, secondary: "" }
                    : updated.phone,
                terminals: Array.isArray(updated.terminals)
                    ? updated.terminals.map((t: any) =>
                        typeof t === "string"
                            ? { terminalId: t, name: "Unknown" }
                            : { terminalId: t.terminalId, name: t.name ?? "Unknown" }
                    )
                    : [],
            }

            // Update state
            setFranchiseUsers((prev) =>
                prev.map((u) =>
                    u.email === normalizedUser.email
                        ? normalizedUser
                        : u
                )
            )

            setEditMode(false)
            setDialogOpen(false)
            setUpdateSuccessOpen(true)
        } catch (err) {
            console.error("Failed to update user", err)
            setError("Failed to update user. Please try again.")
        } finally {
            setApiLoading(false)
        }
    }

    useEffect(() => {
        if (updateSuccessOpen || deleteSuccessOpen) {
            const timer = setTimeout(() => {
                setUpdateSuccessOpen(false)
                setDeleteSuccessOpen(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [updateSuccessOpen, deleteSuccessOpen])

    // Deleting Franchise Users
    const handleConfirmDelete = (email: string) => {
        setSelectedUserEmail(email)
        setDeleteDialogOpen(true)
    }

    const handleDeleteUser = async () => {
        if (!selectedUserEmail) return
        try {
            setApiLoading(true)
            await franchiseUserService.deleteSupportUsers(selectedUserEmail)

            const updated = franchiseUsers.filter(user => user.email !== selectedUserEmail)
            setFranchiseUsers(updated)
            sessionService.setFranchiseUserBrief(updated)

            setDeleteDialogOpen(false)
            setSelectedUserEmail(null)

            // Show success popup
            setDeleteSuccessOpen(true)
        } catch (err) {
            logger.error("Failed to delete support user", err)
            setError("Failed to delete user. Please try again.")
        } finally {
            setApiLoading(false)
        }
    }

    useEffect(() => {
        if (updateSuccessOpen || deleteSuccessOpen) {
            const timer = setTimeout(() => {
                setUpdateSuccessOpen(false)
                setDeleteSuccessOpen(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [updateSuccessOpen, deleteSuccessOpen])

    // -------------------------------
    // Filtering logic (safe)
    // -------------------------------
    const filteredUsers = franchiseUsers.filter((user) => {
        const query = searchQuery.toLowerCase().trim()

        const firstname = user.firstname?.toLowerCase() || ""
        const lastname = user.lastname?.toLowerCase() || ""
        const fullName = `${firstname} ${lastname}`.trim()
        const email = user.email?.toLowerCase() || ""
        const terminals = user.terminals?.map((t) => t.name?.toLowerCase() || "") || []
        const role = FranchiseRoleLabels[user.role]?.toLowerCase() || ""
        const status = user.isActive ? "active" : "inactive"

        return (
            firstname.includes(query) ||
            lastname.includes(query) ||
            fullName.includes(query) ||
            email.includes(query) ||
            terminals.some((t) => t.includes(query)) ||
            role.includes(query) ||
            status.includes(query)
        )
    })

    if (pageLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <CircularProgress />
            </div>
        )
    }

    return (
        <div className="flex flex-col p-1">

            <Box
                className="mb-3"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 3,
                }}
            >

                {/* LEFT COLUMN: Franchise Info */}
                <Box sx={{ flex: 1, minWidth: 250 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "gray.800" }}>
                        Franchise Users
                    </Typography>
                </Box>

                {/* RIGHT COLUMN: Stats + Searchbar + Buttons */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                >
                    {/* Searchbar + Buttons in one row */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Searchbar />

                        <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                            Back
                        </Button>

                        <Button variant="contained" onClick={() => setDialogOpen(true)} disabled={apiLoading}>
                            Create Franchise User
                        </Button>
                    </Box>
                </Box>
            </Box>

            <div className="w-full max-h-[500px] overflow-hidden text-sm rounded-lg bg-white shadow-md">
                <div className="max-h-full overflow-y-auto scroll-smooth">
                    <table className="min-w-full table-fixed border-separate border-spacing-0">
                        <thead className="sticky top-0 z-10 bg-gray-200">
                            <tr>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">#</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Name</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Email</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Terminals</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Role</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Status</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Action</th>
                            </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user, idx) => (
                                    <tr key={user.email} className="hover:bg-gray-50 border-b border-gray-200">
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{idx + 1}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{user.firstname} {user.lastname}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{user.email}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            {user.terminals.length > 0 ? (
                                                user.terminals
                                                    .reduce<string[][]>((rows, terminal, index) => {
                                                        if (index % 3 === 0) rows.push([terminal.name])
                                                        else rows[rows.length - 1].push(terminal.name)
                                                        return rows
                                                    }, [])
                                                    .map((row, idx) => (
                                                        <div key={idx}>
                                                            {row.join(", ")}
                                                        </div>
                                                    ))
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{FranchiseRoleLabels[user.role]}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            <span
                                                className="px-3 py-1 text-xs font-medium rounded-full text-white"
                                                style={{ backgroundColor: user.isActive ? COLORS.ACTIVE : COLORS.INACTIVE }}
                                            >
                                                {user.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        {currentUserRole !== SupportRole.SUPPORT && (<td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {/* === EDIT BUTTON LOGIC === */}
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        bgcolor: COLORS.ASH,
                                                        transition: 'background-color 0.2s',
                                                        borderRadius: '50%',
                                                        '&:hover': {
                                                            bgcolor: COLORS.HOVER_ASH,
                                                        }
                                                    }}
                                                    color="primary"
                                                    onClick={() => {
                                                        setEditMode(true)
                                                        setUserToEdit(user)
                                                        setDialogOpen(true)
                                                    }}
                                                >
                                                    <Pencil style={{ fontSize: 12 }} />
                                                </IconButton>

                                                {/* DELETE BUTTON LOGIC Only visible for superadmin login */}
                                                {currentUserRole === SupportRole.SUPERADMIN && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor: COLORS.ASH,
                                                            transition: 'background-color 0.2s',
                                                            borderRadius: '50%',
                                                            '&:hover': {
                                                                bgcolor: COLORS.HOVER_ASH,
                                                            },
                                                        }}
                                                        color="error"
                                                        onClick={() => handleConfirmDelete(user.email)}
                                                    >
                                                        <Trash2 style={{ fontSize: 12 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <TableCell colSpan={6} align="center">
                                        No franchise users found.
                                    </TableCell>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create New Franchise User</DialogTitle>
                <DialogContent>
                    {apiLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <CreateFranchiseUserForm franchiseId={franchiseId} currentUserRole={currentUserRole!} terminals={terminals} onSubmit={handleCreateFranchiseUser} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editMode ? "Update Franchise User" : "Create New Franchise User"}
                </DialogTitle>
                <DialogContent>
                    {apiLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : editMode && userToEdit ? (
                        <UpdateFranchiseUserForm
                            defaultValues={{
                                email: userToEdit.email,
                                firstname: userToEdit.firstname,
                                lastname: userToEdit.lastname,
                                role: userToEdit.role,
                                terminals: userToEdit.terminals.map(t => t.terminalId),
                                isActive: userToEdit.isActive,
                                address: userToEdit.address ?? {
                                    street: "",
                                    city: "",
                                    state: "",
                                    postalCode: ""
                                },
                                phone: {
                                    primary: (userToEdit.phone as any)?.primary ?? (typeof userToEdit.phone === "string" ? userToEdit.phone : ""),
                                    secondary: (userToEdit.phone as any)?.secondary ?? ""
                                },
                                franchiseId: userToEdit.franchiseId,
                            }}
                            terminals={terminals}
                            onSubmit={handleUpdateFranchiseUser}
                            onCancel={() => setEditMode(false)}
                            isLoading={apiLoading}
                        />
                    ) : (
                        <CreateFranchiseUserForm
                            franchiseId={franchiseId}
                            currentUserRole={currentUserRole!}
                            terminals={terminals}
                            onSubmit={handleCreateFranchiseUser}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDialog} color="secondary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Show Error Dialog Box if Fields are Empty */}
            <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
                <DialogTitle style={{ color: COLORS.RED }}>Error</DialogTitle>
                <DialogContent>
                    <Typography>{errorMessage}</Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this Franchise user?</Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>

                    <Button
                        onClick={handleDeleteUser}
                        color="error"
                        disabled={apiLoading}
                        variant="contained"
                    >
                        {apiLoading ? <CircularProgress size={20} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteSuccessOpen}
                onClose={() => setDeleteSuccessOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: COLORS.LIGHT_GREEN,
                            margin: "0 auto 16px auto",
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Franchise User Deleted Successfully.
                    </Typography>
                </DialogContent>
            </Dialog>

            <Dialog
                open={updateSuccessOpen}
                onClose={() => setUpdateSuccessOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: COLORS.LIGHT_GREEN,
                            margin: "0 auto 16px auto"
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Franchise User Updated Successfully.
                    </Typography>
                </DialogContent>
            </Dialog>

            {error && (
                <Box sx={{ mt: 2 }} className="text-center text-red-600">
                    {error}
                </Box>
            )}
        </div>
    )
}

export default FranchiseUsers